/**
 * The visit's room, named from the provider app's side.
 *
 * `@repo/conferencing` deliberately does not know the word "visit" — its public
 * surface test bans clinical nouns outright — so the scope word is spelled by
 * each end independently. That is the arrangement working as designed, and it
 * is also its one sharp edge: the server spells it in
 * `packages/api/src/queries/join-visit-room.query.ts` (`VISIT_ROOM_SCOPE`), and
 * if the two ever disagree the clinician and the patient each open a room, each
 * connect successfully, and neither can see or hear the other. Nothing throws,
 * nothing logs, and both surfaces read "Connected".
 *
 * `__tests__/visit-room.test.ts` reads the server's constant out of that file
 * and fails the PR if this one drifts from it.
 */

import { type RoomKey, roomKey } from "@repo/conferencing";
import { enterCall, leaveCall } from "@repo/conferencing/components";

/** Must equal `VISIT_ROOM_SCOPE` in `join-visit-room.query.ts`. Guarded by test. */
export const VISIT_ROOM_SCOPE = "visit";

export const visitRoomKey = (visitId: string): RoomKey => roomKey(VISIT_ROOM_SCOPE, visitId);

/**
 * The visit id a room key is for, or `undefined` when the key is not a visit's.
 * The call store is one store for the whole app; a host that assumed every key
 * it saw was a visit's would mis-address the first non-visit room to land in it.
 */
export function visitIdFromRoomKey(key: RoomKey): string | undefined {
  const prefix = `${VISIT_ROOM_SCOPE}:`;
  return key.startsWith(prefix) ? key.slice(prefix.length) || undefined : undefined;
}

/**
 * Open the visit's room as the clinician.
 *
 * Deliberately takes no visit status: the clinician opens the room while the
 * visit may still be `booked` and waits in it, which is the whole point of
 * admitting from inside the call rather than from the schedule.
 */
export const openVisitRoom = (visitId: string): void => enterCall(visitRoomKey(visitId), "host");

/** Close it. Keyed, so a stale caller cannot close the room already moved to. */
export const closeVisitRoom = (visitId: string): void => leaveCall(visitRoomKey(visitId));
